import { Fragment, useEffect, useState } from 'react';
import { api } from '../lib/api';

interface AuditRun {
  id: number;
  status: 'pending' | 'running' | 'complete' | 'failed' | 'cancelled';
  scope_states: string[] | null;
  scope_af_numbers: string[] | null;
  /** Territories/sites covered, "AF-262 / AF-262.2 (10418)"; null = not recorded. */
  sites_included: string[] | null;
  audit_window_start: string;
  audit_window_end: string;
  spec_version: string | null;
  pairs_total: number | null;
  pairs_evaluated: number | null;
  cases_opened: number | null;
  cases_skipped_suppressed: number | null;
  cases_auto_cleared: number | null;
  force_rerun_active: boolean;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  triggered_by: { user_id: number; email: string | null; name: string | null };
}

interface RunsProps {
  /** Jump to the case queue scoped to this run's results. */
  onViewResults?: (f: { id: number; label: string }) => void;
}

export function Runs({ onViewResults }: RunsProps) {
  const [rows, setRows] = useState<AuditRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openSites, setOpenSites] = useState<Set<number>>(new Set());
  const toggleSites = (id: number) => setOpenSites((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  useEffect(() => {
    api<{ runs: AuditRun[] }>('/api/audit-runs?limit=100')
      .then((d) => setRows(d.runs))
      .catch((e) => setError(e instanceof Error ? e.message : 'load failed'));
  }, []);

  return (
    <div className="scroll-y" style={{ flex: 1, padding: 24 }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>History</div>
        <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 600 }}>Audit runs</h1>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
          Every audit run, freshest first. Includes manual triggers from the case queue, scheduled runs, and audit-job orchestrator runs.
        </div>
      </div>
      {error && <div className="card card-pad c-warn" style={{ fontSize: 13 }}>Failed to load: {error}</div>}
      {!rows && !error && <div className="card card-pad muted" style={{ fontSize: 13 }}>Loading…</div>}
      {rows && rows.length === 0 && <div className="card card-pad muted" style={{ fontSize: 13 }}>No audit runs yet. Trigger one from the case queue.</div>}
      {rows && rows.length > 0 && (
        <div className="card">
          <table className="tbl">
            <thead>
              <tr>
                <th>Run</th>
                <th>Trigger</th>
                <th>Scope</th>
                <th>Territories</th>
                <th>Window</th>
                <th style={{ textAlign: 'right' }}>Pairs</th>
                <th style={{ textAlign: 'right' }}>Opened</th>
                <th style={{ textAlign: 'right' }}>Suppressed</th>
                <th style={{ textAlign: 'right' }}>Cleared</th>
                <th>Runtime</th>
                <th>Status</th>
                <th>Started</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const runtime = r.started_at && r.completed_at
                  ? msToText(new Date(r.completed_at).getTime() - new Date(r.started_at).getTime())
                  : r.status === 'running' ? 'in progress' : '—';
                const trigger = r.triggered_by?.name ?? r.triggered_by?.email ?? `User #${r.triggered_by?.user_id ?? '?'}`;
                const scope = r.scope_af_numbers && r.scope_af_numbers.length > 0
                  ? r.scope_af_numbers.join(', ')
                  : (r.scope_states ?? []).join(', ') || 'all';
                return (
                  <Fragment key={r.id}>
                  <tr>
                    <td className="mono" style={{ fontSize: 12.5 }}>
                      RUN-{r.id}
                      {r.force_rerun_active && (
                        <span title="Run with suppressions ignored — suppressed pairs were evaluated, and dismissed cases were not auto-reopened" style={{
                          marginLeft: 6, fontSize: 9.5, padding: '1px 5px', borderRadius: 3,
                          background: 'var(--warn-soft)', color: 'var(--warn)', fontWeight: 600,
                        }}>FORCED</span>
                      )}
                    </td>
                    <td>{trigger}</td>
                    <td>{scope}</td>
                    <td style={{ fontSize: 12 }}>
                      {r.sites_included
                        ? <button className="btn btn-sm btn-ghost" onClick={() => toggleSites(r.id)} title="Show the territories and AxisCare sites in this run">
                            {r.sites_included.length} site{r.sites_included.length === 1 ? '' : 's'} {openSites.has(r.id) ? '▾' : '▸'}
                          </button>
                        : <span className="faint" title="Runs before 20 Oct 2026 did not record their sites">not recorded</span>}
                    </td>
                    <td className="mono" style={{ fontSize: 11.5 }}>
                      {fmtDate(r.audit_window_start)} → {fmtDate(r.audit_window_end)}
                    </td>
                    <td className="num mono">{r.pairs_evaluated ?? '—'}</td>
                    <td className="num mono">{r.cases_opened ?? '—'}</td>
                    <td className="num mono muted">{r.cases_skipped_suppressed ?? 0}</td>
                    <td className="num mono muted">{r.cases_auto_cleared ?? 0}</td>
                    <td className="num mono">{runtime}</td>
                    <td><StatusPill status={r.status} /></td>
                    <td className="muted mono" style={{ fontSize: 12 }}>{r.completed_at ? ago(r.completed_at) : ago(r.created_at)}</td>
                    <td>
                      {r.status === 'complete' && onViewResults && (
                        <button
                          className="btn btn-sm"
                          title={`Open the case queue scoped to RUN-${r.id}'s results`}
                          onClick={() => onViewResults({ id: r.id, label: scope })}
                        >
                          View results
                        </button>
                      )}
                    </td>
                  </tr>
                  {openSites.has(r.id) && r.sites_included && (
                    <tr>
                      <td colSpan={13} style={{ background: 'var(--surface-2)', fontSize: 12, lineHeight: 1.7 }}>
                        <strong>Territories included in RUN-{r.id} ({r.sites_included.length} sites):</strong>{' '}
                        <span className="mono">{r.sites_included.join(', ')}</span>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: AuditRun['status'] }) {
  const dotColor = status === 'complete' ? 'ok'
    : status === 'failed' || status === 'cancelled' ? 'danger'
    : status === 'running' ? 'info'
    : 'warn';
  const label = status[0].toUpperCase() + status.slice(1);
  return (
    <span className="pill"><span className={`pill-dot dot-${dotColor}`}></span>{label}</span>
  );
}

function fmtDate(s: string): string {
  const d = new Date(s);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function ago(iso: string): string {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function msToText(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r > 0 ? `${m}m ${r}s` : `${m}m`;
}

